import {
  BarChart3,
  ClipboardList,
  Download,
  FilterX,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import WorkspaceLayout from '../../layout/WorkspaceLayout';
import SelectField from '../../components/Forms/Input/SelectField';
import DatePicker from '../../components/Forms/DatePicker/DatePicker';
import InputField from '../../components/Forms/Input/InputField';
import useRestaurantReports from '../../hooks/restaurant/useRestaurantReports';
import {
  RestaurantReportFilterConfig,
  RestaurantReportTypeDTO,
} from '../../types/restaurant/RestaurantReportTypes';

const categoryTone: Record<string, string> = {
  Ventas: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  Operacion: 'bg-sky-50 text-sky-700 ring-sky-200',
  Menu: 'bg-amber-50 text-amber-700 ring-amber-200',
  Inventario: 'bg-violet-50 text-violet-700 ring-violet-200',
  Compras: 'bg-rose-50 text-rose-700 ring-rose-200',
  Costos: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  Cocina: 'bg-orange-50 text-orange-700 ring-orange-200',
  Reservas: 'bg-cyan-50 text-cyan-700 ring-cyan-200',
};

const ReportCard = ({
  report,
  selected,
  onSelect,
}: {
  report: RestaurantReportTypeDTO;
  selected: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    onClick={onSelect}
    className={`w-full rounded-lg border bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md dark:bg-boxdark ${
      selected
        ? 'border-primary ring-2 ring-primary/20 dark:border-primary'
        : 'border-stroke dark:border-strokedark'
    }`}
  >
    <div className="flex items-start gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <BarChart3 className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-black dark:text-white">
          {report.name}
        </span>
        <span className="mt-1 line-clamp-2 block text-xs leading-5 text-bodydark2">
          {report.description}
        </span>
      </span>
    </div>
    <span
      className={`mt-4 inline-flex rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${
        categoryTone[report.category] ||
        'bg-gray-2 text-body ring-stroke dark:bg-meta-4'
      }`}
    >
      {report.category}
    </span>
  </button>
);

const ReportField = ({
  field,
  value,
  onChange,
}: {
  field: RestaurantReportFilterConfig & {
    options?: { id: string | number; name: string }[];
  };
  value: string;
  onChange: (value: string) => void;
}) => {
  if (field.type === 'date') {
    return (
      <DatePicker
        label={field.label}
        name={field.name}
        value={value}
        onChange={onChange}
        required={field.required}
        iconKey="calendar"
        withMargin={false}
      />
    );
  }

  if (field.type === 'select') {
    return (
      <SelectField
        label={field.label}
        name={field.name}
        value={value}
        onChange={(event) => onChange(String(event.target.value))}
        options={(field.options || []).map((option) => ({
          value: option.id,
          label: option.name,
        }))}
        placeholder="Todos"
        required={field.required}
        iconKey="list"
        withMargin={false}
      />
    );
  }

  return (
    <InputField
      label={field.label}
      name={field.name}
      type={field.type === 'number' ? 'number' : 'text'}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={field.placeholder || field.label}
      required={field.required}
    />
  );
};

const RestaurantReports = () => {
  const reports = useRestaurantReports();
  const [query, setQuery] = useState('');

  const filteredReports = useMemo(() => {
    const term = query.trim().toLowerCase();

    if (!term) return reports.reports;

    return reports.reports.filter((report) =>
      `${report.name} ${report.description} ${report.category}`
        .toLowerCase()
        .includes(term)
    );
  }, [query, reports.reports]);

  const selectedReportSummary = reports.selectedReport
    ? `${reports.selectedReport.category} | ${reports.selectedReport.code}`
    : 'Seleccione un reporte para ver sus parametros';

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <div className="mt-6 space-y-5">
        <section className="rounded-xl border border-stroke bg-white px-5 py-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                Inteligencia del restaurante
              </p>
              <h1 className="mt-1 text-2xl font-bold text-black dark:text-white">
                Reportes del restaurante
              </h1>
              <p className="mt-1 max-w-3xl text-sm text-bodydark2">
                Analice ventas, ordenes, materia prima, compras, cocina y
                reservaciones desde una sola pantalla.
              </p>
            </div>

            <div
              className={`grid gap-3 xl:min-w-[520px] ${
                reports.canPickBranch ? 'sm:grid-cols-2' : 'sm:grid-cols-1'
              }`}
            >
              {reports.canPickBranch && (
                <SelectField
                  label="Sucursal"
                  name="branchId"
                  iconKey="store"
                  value={reports.branchId}
                  disabled={reports.loading}
                  onChange={(event) =>
                    reports.setBranchId(Number(event.target.value))
                  }
                  options={reports.branches.map((branch) => ({
                    value: branch.id,
                    label: branch.name,
                  }))}
                  withMargin={false}
                />
              )}

              <SelectField
                label="Formato"
                name="format"
                iconKey="file"
                value={reports.format}
                onChange={(event) =>
                  reports.setFormat(event.target.value as 'XLSX' | 'PDF')
                }
                options={[
                  { value: 'XLSX', label: 'Excel' },
                  { value: 'PDF', label: 'PDF' },
                ]}
                withMargin={false}
              />
            </div>
          </div>

          {reports.error && (
            <div className="mt-4 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
              {reports.error}
            </div>
          )}
        </section>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.1fr)_minmax(390px,0.9fr)]">
          <section className="rounded-xl border border-stroke bg-white p-5 shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-black dark:text-white">
                  Catalogo de reportes
                </h2>
                <p className="mt-1 text-sm text-bodydark2">
                  Elija el analisis que quiere generar.
                </p>
              </div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-bodydark2" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Buscar reporte"
                  className="h-10 w-full rounded-lg border border-stroke bg-transparent pl-9 pr-3 text-sm outline-none focus:border-primary dark:border-strokedark dark:bg-boxdark sm:w-64"
                />
              </div>
            </div>

            {reports.loading ? (
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-36 animate-pulse rounded-lg bg-gray-2 dark:bg-meta-4"
                  />
                ))}
              </div>
            ) : (
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {filteredReports.map((report) => (
                  <ReportCard
                    key={report.id}
                    report={report}
                    selected={reports.selectedReportId === report.id}
                    onSelect={() => {
                      reports.setSelectedReportId(report.id);
                      reports.resetFilters();
                    }}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="border-b border-stroke p-5 dark:border-strokedark">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <ClipboardList className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold text-black dark:text-white">
                    Parametros
                  </h2>
                  <p className="mt-1 break-words text-sm text-bodydark2">
                    {selectedReportSummary}
                  </p>
                </div>
              </div>
            </div>

            {!reports.selectedReport ? (
              <div className="flex min-h-[420px] flex-col items-center justify-center px-6 text-center">
                <BarChart3 className="mb-3 h-11 w-11 text-bodydark2" />
                <p className="font-medium text-black dark:text-white">
                  Seleccione un reporte
                </p>
                <p className="mt-1 max-w-sm text-sm text-bodydark2">
                  Al elegirlo se muestran los filtros necesarios para que el
                  backend genere el archivo.
                </p>
              </div>
            ) : (
              <div className="space-y-5 p-5">
                {reports.optionsLoading && (
                  <div className="inline-flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Actualizando opciones
                  </div>
                )}

                <div className="grid gap-4 sm:grid-cols-2">
                  {reports.filtersWithOptions.map((field) => (
                    <ReportField
                      key={field.name}
                      field={field}
                      value={reports.filters[field.name] || ''}
                      onChange={(value) =>
                        reports.updateFilter(field.name, value)
                      }
                    />
                  ))}
                </div>

                <div className="rounded-lg border border-stroke bg-gray-2 p-4 text-sm text-bodydark2 dark:border-strokedark dark:bg-meta-4">
                  <p className="font-medium text-black dark:text-white">
                    Salida esperada
                  </p>
                  <p className="mt-1">
                    El backend recibira codigo de reporte, sucursal, formato y
                    filtros. La respuesta debe ser un archivo descargable con
                    Content-Disposition.
                  </p>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={reports.resetFilters}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-stroke px-4 text-sm font-medium text-black hover:bg-gray-2 dark:border-strokedark dark:text-white dark:hover:bg-meta-4"
                  >
                    <FilterX className="h-4 w-4" />
                    Limpiar filtros
                  </button>
                  <button
                    type="button"
                    onClick={reports.clearSelection}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-danger px-4 text-sm font-medium text-white hover:bg-opacity-90"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Restablecer
                  </button>
                  <button
                    type="button"
                    onClick={() => void reports.generateReport()}
                    disabled={reports.exporting}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {reports.exporting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    {reports.exporting ? 'Generando…' : 'Generar reporte'}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </WorkspaceLayout>
  );
};

export default RestaurantReports;
